import React, { useState } from 'react';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import { Project, portfolioProjects } from '../data/portfolioData';

interface ProjectIndexTableProps {
  onSelectProject: (project: Project) => void;
}

export const ProjectIndexTable: React.FC<ProjectIndexTableProps> = ({ onSelectProject }) => {
  const [hoveredProject, setHoveredProject] = useState<Project | null>(null);

  return (
    <section id="index" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 scroll-mt-24">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-10 border-b border-white/10">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-mono text-arch-accent tracking-widest uppercase">
            <BookOpen className="w-3.5 h-3.5" />
            <span>04 // Comprehensive Archives</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif text-white tracking-tight">
            Monograph <span className="italic font-normal text-arch-limestone">Index</span>
          </h2>
          <p className="text-xs sm:text-sm text-neutral-400 font-light max-w-lg">
            Chronological register of architectural commissions, built works, and speculative studies.
          </p>
        </div>

        <div className="text-xs font-mono text-neutral-500">
          INDEX RANGE: 2021 — 2025 // TOTAL {portfolioProjects.length} RECORDS
        </div>
      </div>

      {/* Interactive Monograph Table */}
      <div className="relative mt-8 overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/15 text-[10px] font-mono text-arch-muted uppercase tracking-widest">
              <th className="py-4 px-3 font-normal">No.</th>
              <th className="py-4 px-4 font-normal">Project & Typology</th>
              <th className="py-4 px-4 font-normal hidden sm:table-cell">Location</th>
              <th className="py-4 px-4 font-normal hidden md:table-cell">Footprint Area</th>
              <th className="py-4 px-4 font-normal">Year</th>
              <th className="py-4 px-4 font-normal hidden lg:table-cell">Status</th>
              <th className="py-4 px-3 text-right font-normal">Inspect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-xs font-mono">
            {portfolioProjects.map((project) => (
              <tr
                key={project.id}
                onMouseEnter={() => setHoveredProject(project)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => onSelectProject(project)}
                className="group cursor-pointer hover:bg-white/[0.03] transition-colors"
              >
                <td className="py-5 px-3 text-arch-accent font-semibold">
                  {project.id}
                </td>
                <td className="py-5 px-4">
                  <div className="text-base font-serif text-white group-hover:text-arch-accent transition-colors font-medium">
                    {project.title}
                  </div>
                  <div className="text-[11px] text-neutral-400 font-sans sm:hidden pt-0.5">
                    {project.location} • {project.category}
                  </div>
                  <div className="text-[11px] text-neutral-500 hidden sm:block">
                    {project.specs.typology}
                  </div>
                </td>
                <td className="py-5 px-4 text-neutral-300 hidden sm:table-cell">
                  {project.location}
                </td>
                <td className="py-5 px-4 text-neutral-400 hidden md:table-cell">
                  {project.area}
                </td>
                <td className="py-5 px-4 text-white">
                  {project.year}
                </td>
                <td className="py-5 px-4 hidden lg:table-cell">
                  <span className={`px-2 py-0.5 rounded text-[10px] border ${
                    project.status === 'Completed'
                      ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5'
                      : 'border-amber-500/30 text-amber-400 bg-amber-500/5'
                  }`}>
                    {project.status}
                  </span>
                </td>
                <td className="py-5 px-3 text-right">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/5 group-hover:bg-arch-accent group-hover:text-[#0A0B0D] text-neutral-400 transition-colors">
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Floating Hover Image Preview */}
        {hoveredProject && (
          <div className="hidden xl:block fixed pointer-events-none z-30 bottom-16 right-16 w-80 aspect-[16/10] rounded overflow-hidden border border-white/20 shadow-2xl bg-black">
            <img
              src={hoveredProject.heroImage}
              alt={hoveredProject.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 text-xs font-mono text-white">
              <div className="text-arch-accent font-semibold">{hoveredProject.title}</div>
              <div className="text-[10px] text-neutral-300">{hoveredProject.location} • {hoveredProject.year}</div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
